import constructionCaseThumb02Image from '@/assets/images/hero/hvac-engineering.webp';
import popupExampleImage from '@/assets/images/popup-example.webp';
import constructionCaseGallery06Image from '@/assets/images/refrigeration-system/engineering/engineering-card.webp';
import constructionCaseThumb01Image from '@/assets/images/refrigeration-system/engineering/engineering-card.webp';
import type {
  AdminConstructionCaseSummary,
  AdminInquiryDetail,
  AdminInquiryProcessInput,
  AdminInquirySummary,
  AdminLoginResponse,
  AdminPopupSummary,
  AdminProjectRecordSummary,
  AdminSupportMaterialSummary,
  ConstructionCaseDetail,
  ConstructionCaseImagePlacement,
  ConstructionCaseMutationInput,
  ConstructionCaseSummary,
  PopupDetail,
  PopupSummary,
  PopupMutationInput,
  ProjectRecordDetail,
  ProjectRecordMutationInput,
  ProjectRecordSummary,
  SupportMaterialDetail,
  SupportMaterialImage,
  SupportMaterialMutationInput,
  SupportMaterialSummary,
} from '@/features/board/api';
import type { ProjectRecordCategory } from '@/features/board/projectRecordCategory';

type MockPopup = PopupDetail;
type MockConstructionCase = ConstructionCaseDetail;
type MockProjectRecord = ProjectRecordDetail;
type MockSupportMaterial = SupportMaterialDetail;
type MockInquiry = AdminInquiryDetail;

const mockAdminAccessToken = 'mock-admin-access-token';
const technicalGuidePdfUrl = encodeURI('/website-renewal-guide-260202.pdf');
const technicalCatalogPdfUrl = encodeURI('/kookje-tnc-catalog-v2-25-08-05.pdf');
const technicalBrochurePdfUrl = encodeURI('/mechanical-facilities-brochure.pdf');
const technicalEducationVideoUrls = [
  'https://www.youtube.com/watch?v=education-material-01',
  'https://www.youtube.com/watch?v=education-material-02',
  'https://www.youtube.com/watch?v=education-material-03',
] as const;
const constructionCaseThumb01 = constructionCaseThumb01Image;
const constructionCaseThumb02 = constructionCaseThumb02Image;
const constructionCaseGallery04 = constructionCaseThumb02Image;
const constructionCaseGallery05 = constructionCaseThumb01Image;
const constructionCaseGallery06 = constructionCaseGallery06Image;
const projectRecordThumb03 = constructionCaseThumb01Image;
const defaultConstructionCaseImagePlacement = {
  imageFit: 'contain',
  imageObjectPosition: 'center center',
  imageScale: 1,
} satisfies ConstructionCaseImagePlacement;
const resolveMock = <T>(value: T): Promise<T> => Promise.resolve(value);
const rejectMock = (message: string): Promise<never> => Promise.reject(new Error(message));

const isEducationMaterialCategory = (category: string) => category === '교육자료';

const toSupportMaterialCategoryCode = (category: string) => {
  return isEducationMaterialCategory(category) ? 'EDUCATION_MATERIAL' : 'TECHNICAL_DATA';
};

interface ProjectRecordSeed {
  clientName: string;
  no: number;
  periodLabel: string;
  title: string;
}

const inferProjectRecordCategory = (clientName: string): ProjectRecordCategory => {
  if (/공사|공단|시청|구청|군청|도청|교육청/.test(clientName)) {
    return '공공기관';
  }

  if (/학교|초등|중학교|고등|대학교|교육/.test(clientName)) {
    return '교육기관';
  }

  if (/물류|센터/.test(clientName)) {
    return '물류기업';
  }

  if (/마트|쇼핑|슈퍼|백화점|이마트|신세계|롯데|하나로/.test(clientName)) {
    return '유통기업';
  }

  return '일반기업';
};

const projectRecordThumbnailPool = [
  {
    fileName: 'project-record-thumb-01.jpg',
    fileUrl: constructionCaseThumb01,
  },
  {
    fileName: 'project-record-thumb-02.jpg',
    fileUrl: constructionCaseThumb02,
  },
  {
    fileName: 'project-record-thumb-03.jpg',
    fileUrl: projectRecordThumb03,
  },
] as const;

const getLastDayOfMonth = (year: number, month: number) => {
  return new Date(year, month, 0).getDate();
};

const parseProjectRecordPeriodLabel = (periodLabel: string) => {
  const match = periodLabel.match(
    /(?<year>\d{4})년\s*(?<month>\d{2})월(?:~\s*(?<endMonth>\d{2})월)?/u,
  );

  if (!match?.groups) {
    return null;
  }

  const year = Number(match.groups['year']);
  const month = Number(match.groups['month']);
  const endMonth = match.groups['endMonth'] ? Number(match.groups['endMonth']) : month;

  return {
    endMonth,
    month,
    year,
  };
};

const createProjectRecordPeriod = (
  periodLabel: string,
  orderInMonth: number,
): Pick<MockProjectRecord, 'constructionEndDate' | 'constructionStartDate' | 'publishedAt'> => {
  const period = parseProjectRecordPeriodLabel(periodLabel);

  if (!period) {
    return {
      publishedAt: '2022-01-01T09:00:00',
    };
  }

  const { endMonth, month, year } = period;
  const day = String(Math.max(1, 31 - orderInMonth)).padStart(2, '0');
  const lastDay = String(getLastDayOfMonth(year, endMonth)).padStart(2, '0');

  return {
    constructionEndDate: `${String(year)}-${String(endMonth).padStart(2, '0')}-${lastDay}`,
    constructionStartDate: `${String(year)}-${String(month).padStart(2, '0')}-01`,
    publishedAt: `${String(year)}-${String(month).padStart(2, '0')}-${day}T09:00:00`,
  };
};

let mockPopups: MockPopup[] = [
  {
    id: 1,
    title: '국제티엔씨 홈페이지 리뉴얼 1차 오픈 안내',
    summary: '시공사례, 공사실적, 기술자료 메뉴가 먼저 오픈되었습니다.',
    linkUrl: '/support-materials',
    imageUrl: popupExampleImage,
    imageFit: 'contain',
    imageObjectPosition: 'center center',
    imageScale: 1,
    imageFileName: 'popup-example.webp',
    imageContentType: 'image/webp',
    imageFileSize: 0,
    isPinned: true,
    publishedAt: '2026-04-03T09:00:00',
    content:
      '국제티엔씨 홈페이지 리뉴얼 1차 오픈 안내입니다.\n\n이번 배포에서는 실적·정보지원 영역의 주요 게시판이 우선 반영되었습니다.\n- 시공사례\n- 공사실적\n- 기술자료\n- 웹카탈로그\n\n세부 기능은 단계적으로 확장 예정입니다.',
    isPublished: true,
  },
  {
    id: 2,
    title: '기계설비 브로슈어 최신본 업데이트',
    summary: '기술자료 게시판에서 최신 PDF를 확인할 수 있습니다.',
    linkUrl: '/web-catalogs',
    imageUrl: popupExampleImage,
    imageFit: 'contain',
    imageObjectPosition: 'center center',
    imageScale: 1,
    imageFileName: 'popup-example.webp',
    imageContentType: 'image/webp',
    imageFileSize: 0,
    isPinned: false,
    publishedAt: '2026-04-01T14:30:00',
    content:
      '기계설비 브로슈어 최신본이 반영되었습니다.\n\n영업 제안 및 현장 설명 자료로 활용할 수 있으며, 관련 PDF는 기술자료 게시판에서 내려받을 수 있습니다.',
    isPublished: true,
  },
  {
    id: 3,
    title: '관리자 화면 검토용 임시 팝업',
    summary: '관리자 화면 확인을 위한 비공개 샘플 팝업입니다.',
    linkUrl: '',
    imageUrl: popupExampleImage,
    imageFit: 'contain',
    imageObjectPosition: 'center center',
    imageScale: 1,
    imageFileName: 'popup-example.webp',
    imageContentType: 'image/webp',
    imageFileSize: 0,
    isPinned: false,
    publishedAt: '2026-04-04T10:00:00',
    content:
      '이 팝업은 관리자 화면 검토용으로만 등록된 샘플입니다.\n\n비공개 상태에서는 실제 메인 화면에 표시되지 않습니다.',
    isPublished: false,
  },
];

let mockConstructionCases: MockConstructionCase[] = [
  {
    id: 1,
    slug: 'hanam-logistics-cold-storage',
    title: '하남 복합물류센터 냉동·냉장 설비 시공',
    summary: '저온 물류 구역과 상온 구역을 분리한 복합물류센터 시공사례입니다.',
    category: '냉동·냉장 설비',
    clientName: '하남 물류 운영법인',
    constructionStartDate: '2026-03-08',
    constructionEndDate: '2026-03-27',
    location: '경기도 하남시',
    overview:
      '하남 복합물류센터의 냉동·냉장 설비를 재구성하여 저온 물류 운영 안정성을 높인 프로젝트입니다.\n기존 현장 운영 흐름을 해치지 않도록 공정과 시운전 일정을 세분화해 단계적으로 시공했습니다.',
    scopeItems: [
      '냉동·냉장 구역 분리 설계 및 장비 배치 조정',
      '주요 배관 라인 시공 및 중앙 제어반 연동',
      '유지보수 동선을 고려한 설비 재배치',
      '시운전 및 운영 매뉴얼 제공',
    ],
    featurePoints: [
      {
        title: 'POINT 1',
        description: '저온 물류 운영을 고려한 단계별 시공 진행',
      },
      {
        title: 'POINT 2',
        description: '구역별 부하를 반영한 냉동·냉장 설비 최적화',
      },
      {
        title: 'POINT 3',
        description: '시운전과 매뉴얼 정리까지 포함한 안정화 지원',
      },
    ],
    thumbnailFileUrl: constructionCaseThumb01,
    viewCount: 246,
    publishedAt: '2026-03-27T10:00:00',
    thumbnailFileName: 'con02_img01.jpg',
    thumbnailContentType: 'image/jpeg',
    thumbnailFileSize: 120_000,
    thumbnailFit: 'contain',
    thumbnailObjectPosition: 'center center',
    thumbnailScale: 1,
    content:
      '<p>하남 복합물류센터 현장의 냉동·냉장 설비 시공 사례입니다.</p><p>저온 구역별 부하를 반영한 설비 배치와 유지보수 동선을 함께 고려했습니다.</p><ul><li>냉동·냉장 구역 분리 설계</li><li>중앙 제어반 연동</li><li>시운전 및 운전 매뉴얼 제공</li></ul>',
    isFeatured: true,
    isPublished: true,
    galleryImages: [
      {
        id: 11,
        displayOrder: 1,
        fileName: 'con02_img04.jpg',
        fileUrl: constructionCaseGallery04,
        contentType: 'image/jpeg',
        fileSize: 120_000,
        ...defaultConstructionCaseImagePlacement,
      },
      {
        id: 12,
        displayOrder: 2,
        fileName: 'con02_img05.jpg',
        fileUrl: constructionCaseGallery05,
        contentType: 'image/jpeg',
        fileSize: 120_000,
        ...defaultConstructionCaseImagePlacement,
      },
      {
        id: 13,
        displayOrder: 3,
        fileName: 'con02_img06.jpg',
        fileUrl: constructionCaseGallery06,
        contentType: 'image/jpeg',
        fileSize: 120_000,
        ...defaultConstructionCaseImagePlacement,
      },
    ],
  },
  {
    id: 2,
    slug: 'suwon-food-factory-hvac',
    title: '수원 식품공장 공조기 교체 및 덕트 재구성',
    summary: '생산동 환기 효율을 개선하기 위한 기계·공조설비 시공입니다.',
    category: '기계·공조 설비',
    clientName: '수원 식품공장',
    constructionStartDate: '2026-03-05',
    constructionEndDate: '2026-03-18',
    location: '경기도 수원시',
    overview:
      '생산 공정 열부하를 반영해 공조기와 덕트 라인을 재구성한 프로젝트입니다.\n공정 중단 시간을 최소화하기 위해 야간 작업과 단계별 전환 작업을 병행했습니다.',
    scopeItems: [
      '기존 공조기 철거 및 신규 장비 반입',
      '덕트 라인 재구성 및 배기 흐름 조정',
      '전원·제어 연동 점검 및 운전 확인',
      '야간 시공 중심의 공정 분할 운영',
    ],
    featurePoints: [
      {
        title: 'POINT 1',
        description: '생산 라인 운영을 고려한 야간 교체 공정',
      },
      {
        title: 'POINT 2',
        description: '열부하 대응을 위한 공조 성능 개선',
      },
      {
        title: 'POINT 3',
        description: '덕트 재구성과 제어 연동까지 일괄 대응',
      },
    ],
    thumbnailFileUrl: constructionCaseThumb02,
    viewCount: 133,
    publishedAt: '2026-03-18T13:20:00',
    thumbnailFileName: 'con02_img02.jpg',
    thumbnailContentType: 'image/jpeg',
    thumbnailFileSize: 120_000,
    thumbnailFit: 'contain',
    thumbnailObjectPosition: 'center center',
    thumbnailScale: 1,
    content:
      '<p>생산 공정 열부하를 반영해 공조기와 덕트 라인을 재구성한 사례입니다.</p><p>공정 중단 시간을 최소화하기 위해 야간 시공과 단계별 전환 작업을 병행했습니다.</p>',
    isFeatured: true,
    isPublished: true,
    galleryImages: [
      {
        id: 21,
        displayOrder: 1,
        fileName: 'con02_img06.jpg',
        fileUrl: constructionCaseGallery06,
        contentType: 'image/jpeg',
        fileSize: 120_000,
        ...defaultConstructionCaseImagePlacement,
      },
    ],
  },
  {
    id: 3,
    slug: 'pyeongtaek-utility-piping',
    title: '평택 지원시설 유틸리티 배관 시공',
    summary: '관리자 검토용 비공개 시공사례 샘플입니다.',
    category: '유틸리티 배관',
    clientName: '평택 지원시설',
    constructionStartDate: '2026-03-26',
    constructionEndDate: '2026-04-02',
    location: '경기도 평택시',
    overview:
      '지원시설 유틸리티 배관과 제어 연동을 중심으로 구성한 시공사례 샘플입니다.\n설비실 동선과 유지관리 접근성을 함께 고려해 계획했습니다.',
    scopeItems: [
      '유틸리티 배관 시공 및 지지대 정비',
      '기존 제어부와 연동 상태 점검',
      '운전 안정성 검토 및 단계별 확인',
    ],
    featurePoints: [
      {
        title: 'POINT 1',
        description: '유틸리티 배관 동선 최적화',
      },
      {
        title: 'POINT 2',
        description: '기존 제어 시스템과의 안정적 연동',
      },
      {
        title: 'POINT 3',
        description: '운영 중단을 줄이기 위한 단계별 확인',
      },
    ],
    thumbnailFileUrl: constructionCaseThumb01,
    viewCount: 48,
    publishedAt: '2026-04-02T09:10:00',
    thumbnailFileName: 'con02_img03.jpg',
    thumbnailContentType: 'image/jpeg',
    thumbnailFileSize: 120_000,
    thumbnailFit: 'contain',
    thumbnailObjectPosition: 'center center',
    thumbnailScale: 1,
    content: '<p>지원시설 유틸리티 배관과 제어 연동을 중심으로 한 비공개 샘플 사례입니다.</p>',
    isFeatured: false,
    isPublished: false,
    galleryImages: [],
  },
];

mockConstructionCases = mockConstructionCases.filter((constructionCase) => {
  return !['hanam-logistics-cold-storage', 'suwon-food-factory-hvac'].includes(
    constructionCase.slug,
  );
});

const projectRecordSeeds: ProjectRecordSeed[] = [
  {
    no: 40,
    title: '공조기 리필형 필터 교체',
    clientName: '롯데타워',
    periodLabel: '2025년 12월',
  },
  {
    no: 39,
    title: '화장실 배기휀 커버 교체 (2개소)',
    clientName: 'JYP 엔터테인먼트',
    periodLabel: '2025년 12월',
  },
  {
    no: 38,
    title: '냉동기(콤프레서) 교체 공사',
    clientName: '이마트 경북 물류센터',
    periodLabel: '2025년 12월',
  },
  {
    no: 37,
    title: '냉동저장고 설치 공사',
    clientName: '쿠팡 물류센터',
    periodLabel: '2025년 12월',
  },
  {
    no: 36,
    title: '인버터 냉동기 설치 공사',
    clientName: '롯데쇼핑(주) 경북권',
    periodLabel: '2025년 12월',
  },
  {
    no: 35,
    title: '쇼케이스 개선 공사',
    clientName: '롯데쇼핑(주) 대구권',
    periodLabel: '2025년 12월',
  },
  {
    no: 34,
    title: '냉동기(콤프레서) 설치 공사',
    clientName: '(주)풀무원',
    periodLabel: '2025년 11월',
  },
  {
    no: 33,
    title: '팽창탱크 및 온수배관 교체 공사',
    clientName: '새솔초등학교',
    periodLabel: '2025년 11월',
  },
  {
    no: 32,
    title: '주차장 바닥 외관 펜스 도장 공사',
    clientName: 'JYP 엔터테인먼트',
    periodLabel: '2025년 11월',
  },
  {
    no: 31,
    title: 'VIP 식당 급배기 설비 인버터 설치 공사',
    clientName: 'NC소프트',
    periodLabel: '2025년 11월',
  },
  {
    no: 30,
    title: '직원식당 송풍기 및 배기휀 설치 공사',
    clientName: 'JYP 엔터테인먼트',
    periodLabel: '2025년 11월',
  },
  {
    no: 29,
    title: '냉방장비 세척 및 냉각탑 보수 공사',
    clientName: '이랜드 울산',
    periodLabel: '2025년 11월',
  },
  {
    no: 28,
    title: '화장실 배기휀 모터 교체 공사',
    clientName: 'JYP 엔터테인먼트',
    periodLabel: '2025년 11월',
  },
  {
    no: 27,
    title: '작업장 리뉴얼 공사',
    clientName: '(주)이마트 경북권',
    periodLabel: '2025년 11월',
  },
  {
    no: 26,
    title: '저장고 설치 공사',
    clientName: '롯데쇼핑(주)',
    periodLabel: '2025년 11월',
  },
  {
    no: 25,
    title: '인버터 냉동기 설치 공사',
    clientName: '롯데쇼핑(주) 충북권',
    periodLabel: '2025년 11월',
  },
  {
    no: 24,
    title: '냉장 저장고 신설 공사',
    clientName: '(주)이마트 서울권',
    periodLabel: '2025년 11월',
  },
  {
    no: 23,
    title: '냉동기(콤프레서) 교체 공사',
    clientName: '롯데쇼핑(주) 강원권',
    periodLabel: '2025년 11월',
  },
  {
    no: 22,
    title: '쇼케이스 설치 공사',
    clientName: '농협 하나로마트 충북권',
    periodLabel: '2025년 10월',
  },
  {
    no: 21,
    title: '급수 다단펌프 교체 공사',
    clientName: 'JYP 엔터테인먼트',
    periodLabel: '2025년 10월',
  },
  {
    no: 20,
    title: '직원식당 외 배수관 세척 공사',
    clientName: 'JYP 엔터테인먼트',
    periodLabel: '2025년 10월',
  },
  {
    no: 19,
    title: '냉동기 이설 공사',
    clientName: '롯데쇼핑(주) 서울권',
    periodLabel: '2025년 10월',
  },
  {
    no: 18,
    title: '냉동기(콤프레서) 교체 공사',
    clientName: '(주)이마트 천안터미널점',
    periodLabel: '2025년 10월',
  },
  {
    no: 17,
    title: '저온창고 개선 공사',
    clientName: '성주산업사',
    periodLabel: '2025년 09월',
  },
  {
    no: 16,
    title: '급수펌프 교체 공사',
    clientName: 'JYP 엔터테인먼트',
    periodLabel: '2025년 09월',
  },
  {
    no: 15,
    title: '기계실 보일러 배관 누수 및 안전변 교체 공사',
    clientName: '포포인츠 쉐라톤 서울역 호텔',
    periodLabel: '2025년 09월',
  },
  {
    no: 14,
    title: '주방 배기 점검구 설치 공사',
    clientName: '포포인츠 쉐라톤 서울역 호텔',
    periodLabel: '2025년 09월',
  },
  {
    no: 13,
    title: '메인 급수 수도배관 교체 공사',
    clientName: '안양대학교',
    periodLabel: '2025년 09월',
  },
  {
    no: 12,
    title: '냉동·냉장 설비 개선 공사',
    clientName: '롯데쇼핑(주)',
    periodLabel: '2025년 09월',
  },
  {
    no: 11,
    title: '저온창고 개선 공사',
    clientName: '성주산업사',
    periodLabel: '2025년 08월',
  },
  {
    no: 10,
    title: '유닛 쿨러 설치 공사',
    clientName: '(주)이마트 세종권',
    periodLabel: '2025년 08월',
  },
  {
    no: 9,
    title: '기계실 보일러 스팀트랩 및 안전변 교체 공사',
    clientName: '포포인츠 쉐라톤 서울역 호텔',
    periodLabel: '2025년 08월',
  },
  {
    no: 8,
    title: '지열 배관 교체 공사',
    clientName: '한별중학교',
    periodLabel: '2025년 08월',
  },
  {
    no: 7,
    title: '하나로마트 매장환경 개선 공사',
    clientName: '농협 하나로마트 전남권',
    periodLabel: '2025년 08월',
  },
  {
    no: 6,
    title: '유닛 쿨러 설치 공사',
    clientName: '이마트 트레이더스 송림점',
    periodLabel: '2025년 08월',
  },
  {
    no: 5,
    title: '냉동기(콤프레서) 교체 공사',
    clientName: '(주)이마트 금정점',
    periodLabel: '2025년 08월',
  },
  {
    no: 4,
    title: '냉동기(콤프레서) 교체 공사',
    clientName: '(주)이마트 영등포점',
    periodLabel: '2025년 08월',
  },
  {
    no: 3,
    title: '저온창고 개선 공사',
    clientName: '성주산업사',
    periodLabel: '2025년 07월',
  },
  {
    no: 2,
    title: '21층 EHP 실외기 냉매 누설 보수 공사',
    clientName: '보코호텔',
    periodLabel: '2025년 07월',
  },
  {
    no: 1,
    title: '기계실 급수 팽창탱크 교체 공사',
    clientName: '옥수초등학교',
    periodLabel: '2025년 07월',
  },
  {
    no: 68,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 경남권',
    periodLabel: '2022년 09월',
  },
  {
    no: 67,
    title: '기계설비 유니트 쿨러 설치공사',
    clientName: '(주)이마트 - 경남권',
    periodLabel: '2022년 09월',
  },
  {
    no: 66,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 강원권',
    periodLabel: '2022년 09월',
  },
  {
    no: 65,
    title: '와인샵 설계 및 쇼케이스 설치공사',
    clientName: '(주)신세계엘앤비',
    periodLabel: '2022년 09월',
  },
  {
    no: 64,
    title: '기계설비 수냉식 응축기 교체 설치공사',
    clientName: '홈플러스(주) - 경남권',
    periodLabel: '2022년 09월',
  },
  {
    no: 63,
    title: '기계설비 냉동 Cold Chain 공사',
    clientName: '홈플러스(주) - 경기 서북권',
    periodLabel: '2022년 08월',
  },
  {
    no: 62,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 경기 서남권',
    periodLabel: '2022년 08월',
  },
  {
    no: 61,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 경기 남부권',
    periodLabel: '2022년 08월',
  },
  {
    no: 60,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 충남권',
    periodLabel: '2022년 08월',
  },
  {
    no: 59,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 강남권',
    periodLabel: '2022년 07월',
  },
  {
    no: 58,
    title: '에너지 합리화 사업 냉동기(콤프) 설계 및 시공',
    clientName: '(주)미래비엠',
    periodLabel: '2022년 07월',
  },
  {
    no: 57,
    title: '와인샵 시설정비 보수공사',
    clientName: '(주)신세계엘앤비',
    periodLabel: '2022년 07월',
  },
  {
    no: 56,
    title: '와인샵 설계 및 쇼케이스 설치공사',
    clientName: '디오니소스',
    periodLabel: '2022년 07월',
  },
  {
    no: 55,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 경기 서남권',
    periodLabel: '2022년 07월',
  },
  {
    no: 54,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 충북권',
    periodLabel: '2022년 06월',
  },
  {
    no: 53,
    title: '와인샵 설계 및 쇼케이스 설치공사',
    clientName: '(주)신세계엘앤비',
    periodLabel: '2022년 06월',
  },
  {
    no: 52,
    title: '에너지 합리화 사업 냉동기(콤프) 설계 및 시공',
    clientName: '(주)미래비엠',
    periodLabel: '2022년 06월',
  },
  {
    no: 51,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 부산권',
    periodLabel: '2022년 06월',
  },
  {
    no: 50,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 경기권',
    periodLabel: '2022년 06월',
  },
  {
    no: 49,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 충북권',
    periodLabel: '2022년 06월',
  },
  {
    no: 48,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 서울권',
    periodLabel: '2022년 06월',
  },
  {
    no: 47,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 서울 동북권',
    periodLabel: '2022년 06월',
  },
  {
    no: 46,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 충북권',
    periodLabel: '2022년 06월',
  },
  {
    no: 45,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 경북권',
    periodLabel: '2022년 06월',
  },
  {
    no: 44,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 강원권',
    periodLabel: '2022년 06월',
  },
  {
    no: 43,
    title: '기계설비 냉동기(콤프) 보수공사',
    clientName: '(주)이에스지',
    periodLabel: '2022년 06월',
  },
  {
    no: 42,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 경기 남서권',
    periodLabel: '2022년 06월',
  },
  {
    no: 41,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 경기 남부권',
    periodLabel: '2022년 06월',
  },
  {
    no: 40,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 충북권',
    periodLabel: '2022년 06월',
  },
  {
    no: 39,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 대구권',
    periodLabel: '2022년 05월',
  },
  {
    no: 38,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 강원권',
    periodLabel: '2022년 05월',
  },
  {
    no: 37,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 서울 도심권',
    periodLabel: '2022년 05월',
  },
  {
    no: 36,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 충북권',
    periodLabel: '2022년 05월',
  },
  {
    no: 35,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 충북권',
    periodLabel: '2022년 05월',
  },
  {
    no: 34,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 충북권',
    periodLabel: '2022년 05월',
  },
  {
    no: 33,
    title: '저장고 신축공사',
    clientName: '(주)이마트 - 경기 남동부권',
    periodLabel: '2022년 05월',
  },
  {
    no: 32,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 경기 북서권',
    periodLabel: '2022년 05월',
  },
  {
    no: 31,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 경기 북서부권',
    periodLabel: '2022년 05월',
  },
  {
    no: 30,
    title: '냉매배관 보수공사',
    clientName: '(주)이마트 - 부산권',
    periodLabel: '2022년 05월',
  },
  {
    no: 29,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 대구권',
    periodLabel: '2022년 04월',
  },
  {
    no: 28,
    title: '에너지 합리화 사업 냉동기(콤프) 설계 및 시공',
    clientName: '(주)미래비엠',
    periodLabel: '2022년 04월',
  },
  {
    no: 27,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 서부권',
    periodLabel: '2022년 04월',
  },
  {
    no: 26,
    title: '기계설비 싱글콤프 멀티형 및 유니트 쿨러 설치공사',
    clientName: '(주)에스엔지탑',
    periodLabel: '2022년 04월',
  },
  {
    no: 25,
    title: '기계설비 냉동기(콤프) 교체 및 냉매배관 보수공사',
    clientName: '(주)이마트 - 경기 남동부권',
    periodLabel: '2022년 04월',
  },
  {
    no: 24,
    title: '기계설비 냉동기(콤프) 교체 및 냉매배관 보수공사',
    clientName: '(주)이마트 - 서울 동남권',
    periodLabel: '2022년 04월',
  },
  {
    no: 23,
    title: '쇼케이스 냉매배관 및 부품 교체공사',
    clientName: '롯데쇼핑(주) - 충남권',
    periodLabel: '2022년 03월',
  },
  {
    no: 22,
    title: '에너지 합리화 사업 냉동기(콤프) 설계 및 시공',
    clientName: '(주)미래비엠',
    periodLabel: '2022년 03월',
  },
  {
    no: 21,
    title: '기계설비 냉동기(콤프) 및 유니트 쿨러 설계 및 설치',
    clientName: '(주)이마트 - 경기 남동부권',
    periodLabel: '2022년 03월',
  },
  {
    no: 20,
    title: '와인샵 설계 및 쇼케이스 설치공사',
    clientName: '(주)신세계엘앤비',
    periodLabel: '2022년 03월',
  },
  {
    no: 19,
    title: '와인샵 설계 및 쇼케이스 설치공사',
    clientName: '(주)신세계엘앤비',
    periodLabel: '2022년 03월',
  },
  {
    no: 18,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 서울 동남권',
    periodLabel: '2022년 03월',
  },
  {
    no: 17,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 충남권',
    periodLabel: '2022년 03월',
  },
  {
    no: 16,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 충남권',
    periodLabel: '2022년 03월',
  },
  {
    no: 15,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 광주권',
    periodLabel: '2022년 03월',
  },
  {
    no: 14,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 경기 서남부권',
    periodLabel: '2022년 03월',
  },
  {
    no: 13,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 광주권',
    periodLabel: '2022년 03월',
  },
  {
    no: 12,
    title: '기계설비 냉동기(콤프) 보수공사',
    clientName: '(주)빵방앗간 이마트 안양점',
    periodLabel: '2022년 02월',
  },
  {
    no: 11,
    title: '냉동·냉장 S/C 신규 설치공사',
    clientName: '롯데쇼핑(주) - 광주권',
    periodLabel: '2022년 02월',
  },
  {
    no: 10,
    title: '기계설비 유니트 쿨러 설치공사',
    clientName: '(주)이마트 - 충북권',
    periodLabel: '2022년 02월',
  },
  {
    no: 9,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 전북권',
    periodLabel: '2022년 02월',
  },
  {
    no: 8,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 경기 북서부권',
    periodLabel: '2022년 01월',
  },
  {
    no: 7,
    title: '쇼케이스 이설 및 철거공사',
    clientName: '(주)이마트 - 서울 동남권',
    periodLabel: '2022년 01월',
  },
  {
    no: 6,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 경남권',
    periodLabel: '2022년 01월',
  },
  {
    no: 5,
    title: '냉동·냉장 워크인 설계 및 설치공사',
    clientName: '쿠팡풀필먼트서비스(유)',
    periodLabel: '2022년 01월',
  },
  {
    no: 4,
    title: '기계설비 냉동기(콤프) 부품교체 및 유지보수 공사',
    clientName: '이케아코리아(유)',
    periodLabel: '2022년 01월~12월',
  },
  {
    no: 3,
    title: '기계설비 냉동기(콤프) 유지보수',
    clientName: '홈플러스(주)',
    periodLabel: '2022년 01월~12월',
  },
  {
    no: 2,
    title: '와인샵 시설정비 유지관리',
    clientName: '(주)신세계엘앤비',
    periodLabel: '2022년 01월~12월',
  },
  {
    no: 1,
    title: '냉동·냉장 장애처리서비스 및 유지보수',
    clientName: '홈플러스(주)',
    periodLabel: '2022년 01월~12월',
  },
  {
    no: 90,
    title: '기계설비 냉동기(콤프) 유지관리 및 보수공사',
    clientName: '풀무원 엑소후레쉬물류(주)',
    periodLabel: '2023년 12월',
  },
  {
    no: 89,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트',
    periodLabel: '2023년 12월',
  },
  {
    no: 88,
    title: '환경개선 설계 및 보수공사',
    clientName: '롯데쇼핑(주)- 서울 서북권',
    periodLabel: '2023년 12월',
  },
  {
    no: 87,
    title: '쇼케이스 설계 및 이설공사',
    clientName: '롯데쇼핑(주)- 경기 서북권',
    periodLabel: '2023년 12월',
  },
  {
    no: 86,
    title: '기계설비 냉동•냉장 저장고 전환공사',
    clientName: '롯데쇼핑(주)- 충북권',
    periodLabel: '2023년 12월',
  },
  {
    no: 85,
    title: '쇼케이스 설계 및 설치공사',
    clientName: '롯데쇼핑(주) - 서울 동남권',
    periodLabel: '2023년 12월',
  },
  {
    no: 84,
    title: '기계설비 냉동기(콤프) 및 유니트 쿨러 설치공사',
    clientName: '(주)이마트 - 서울 동부권',
    periodLabel: '2023년 12월',
  },
  {
    no: 83,
    title: '쇼케이스 설계 및 설치공사',
    clientName: '롯데쇼핑(주)- 인천권',
    periodLabel: '2023년 12월',
  },
  {
    no: 82,
    title: '환경개선 설계 및 보수공사',
    clientName: '롯데쇼핑(주)- 경북권',
    periodLabel: '2023년 12월',
  },
  {
    no: 81,
    title: '기계설비 냉동기(콤프) 설치공사',
    clientName: '롯데쇼핑(주)- 경기 동부권',
    periodLabel: '2023년 12월',
  },
  {
    no: 80,
    title: '냉동 저장고 설계 및 설치공사',
    clientName: '롯데쇼핑(주)- 충북권',
    periodLabel: '2023년 12월',
  },
  {
    no: 79,
    title: '환경개선 설계 및 보수공사',
    clientName: '롯데쇼핑(주) - 경북권',
    periodLabel: '2023년 12월',
  },
  {
    no: 78,
    title: '냉장고 문달기 에너지효율화 사업',
    clientName: '켑코이에스(주)',
    periodLabel: '2023년 12월',
  },
  {
    no: 77,
    title: '에스코 도어 설치 유지관리',
    clientName: '롯데쇼핑(주)',
    periodLabel: '2023년 12월',
  },
  {
    no: 76,
    title: '기계설비 군트너 드라이 쿨러 보수공사',
    clientName: '한국전력공사',
    periodLabel: '2023년 12월',
  },
  {
    no: 75,
    title: '환경개선 설계 및 보수공사',
    clientName: '롯데쇼핑(주) - 인천권',
    periodLabel: '2023년 12월',
  },
  {
    no: 74,
    title: '냉동• 냉장 저장고 전환공사',
    clientName: '롯데쇼핑(주)- 인천권',
    periodLabel: '2023년 12월',
  },
  {
    no: 73,
    title: '냉장고문달기 에너지효율화 사업',
    clientName: '켑코이에스(주)',
    periodLabel: '2023년 12월',
  },
  {
    no: 72,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 서울 동북권',
    periodLabel: '2023년 12월',
  },
  {
    no: 71,
    title: '환경개선 설계 및 보수공사',
    clientName: '롯데쇼핑(주) - 인천권',
    periodLabel: '2023년 11월',
  },
  {
    no: 70,
    title: '환경개선 설계 및 보수공사',
    clientName: '롯데쇼핑(주) - 서울 도심권',
    periodLabel: '2023년 11월',
  },
  {
    no: 69,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경기 남부권',
    periodLabel: '2023년 11월',
  },
  {
    no: 68,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경기 남부권',
    periodLabel: '2023년 11월',
  },
  {
    no: 67,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 충북권',
    periodLabel: '2023년 11월',
  },
  {
    no: 66,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 서울 도심권',
    periodLabel: '2023년 11월',
  },
  {
    no: 65,
    title: '쇼케이스 보수공사',
    clientName: '롯데쇼핑(주) - 서울 동북권',
    periodLabel: '2023년 11월',
  },
  {
    no: 64,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 인천권',
    periodLabel: '2023년 10월',
  },
  {
    no: 63,
    title: '환경개선 설계 및 쇼케이스 설치공사',
    clientName: '롯데쇼핑(주) - 서울 도심권',
    periodLabel: '2023년 10월',
  },
  {
    no: 62,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경기남부권',
    periodLabel: '2023년 10월',
  },
  {
    no: 61,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경기 남부권',
    periodLabel: '2023년 10월',
  },
  {
    no: 60,
    title: '기계설비 유니트 쿨러 설치공사',
    clientName: '(주)이마트 - 서울 서남권',
    periodLabel: '2023년 10월',
  },
  {
    no: 59,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경기 남부권',
    periodLabel: '2023년 10월',
  },
  {
    no: 58,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경기 남부권',
    periodLabel: '2023년 10월',
  },
  {
    no: 57,
    title: '기계설비 공냉식 에어컨 설치공사',
    clientName: '롯데쇼핑(주)- 충북권',
    periodLabel: '2023년 10월',
  },
  {
    no: 56,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 경남권',
    periodLabel: '2023년 09월',
  },
  {
    no: 55,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 서울 동북권',
    periodLabel: '2023년 09월',
  },
  {
    no: 54,
    title: '에스코 도어 설치 유지관리',
    clientName: '롯데쇼핑(주) - 서울 동북권',
    periodLabel: '2023년 09월',
  },
  {
    no: 53,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 서울 서남권',
    periodLabel: '2023년 09월',
  },
  {
    no: 52,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 서울 동북권',
    periodLabel: '2023년 09월',
  },
  {
    no: 51,
    title: '쇼케이스 이설공사',
    clientName: '롯데쇼핑(주)- 서울 동남권',
    periodLabel: '2023년 09월',
  },
  {
    no: 50,
    title: '냉장고 문달기 에너지효율화 사업',
    clientName: '켑코이에스(주)',
    periodLabel: '2023년 09월',
  },
  {
    no: 49,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 서울 서남권',
    periodLabel: '2023년 09월',
  },
  {
    no: 48,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 경북권',
    periodLabel: '2023년 09월',
  },
  {
    no: 47,
    title: '트레이더스 수원화서점 유니트 쿨러 납품',
    clientName: '(주)이마트 - 서울 동북권',
    periodLabel: '2023년 08월',
  },
  {
    no: 46,
    title: '현대자동차(평택) 항온항습기 이설공사',
    clientName: '진영도시개발',
    periodLabel: '2023년 08월',
  },
  {
    no: 45,
    title: '냉장고문달기 에너지효율화 사업',
    clientName: '켑코이에스(주)',
    periodLabel: '2023년 08월',
  },
  {
    no: 44,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 경기 동부권',
    periodLabel: '2023년 07월',
  },
  {
    no: 43,
    title: '에너지 합리화 사업 냉동기(콤프) 설계 및 시공',
    clientName: '(주)이마트 - 대구권',
    periodLabel: '2023년 07월',
  },
  {
    no: 42,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경기 동부권',
    periodLabel: '2023년 07월',
  },
  {
    no: 41,
    title: '기계설비 유니트 쿨러 설치공사',
    clientName: '(주)이마트 - 경기 동부권',
    periodLabel: '2023년 07월',
  },
  {
    no: 40,
    title: '냉동•냉장 저장고 전환공사',
    clientName: '(주)이마트 - 강원권',
    periodLabel: '2023년 07월',
  },
  {
    no: 39,
    title: '쇼케이스 이설공사',
    clientName: '롯데쇼핑(주)- 전남권',
    periodLabel: '2023년 07월',
  },
  {
    no: 38,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 전남권',
    periodLabel: '2023년 07월',
  },
  {
    no: 37,
    title: '현대자동차(평택) 항온항습기 이설공사',
    clientName: '진영도시개발',
    periodLabel: '2023년 07월',
  },
  {
    no: 36,
    title: '기계설비 냉동기(콤프) 및 유니트 쿨러 설치공사',
    clientName: '(주)부방유통 이마트 안양점',
    periodLabel: '2023년 07월',
  },
  {
    no: 35,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 경북권',
    periodLabel: '2023년 06월',
  },
  {
    no: 34,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 경기 동부권',
    periodLabel: '2023년 06월',
  },
  {
    no: 33,
    title: '에너지 합리화 사업 냉동기(콤프) 설계 및 시공',
    clientName: '(주)이마트 - 대구권',
    periodLabel: '2023년 06월',
  },
  {
    no: 32,
    title: '냉동 저장고 전환공사',
    clientName: '롯데쇼핑(주)',
    periodLabel: '2023년 06월',
  },
  {
    no: 31,
    title: '냉동•냉장 쇼케이스 설계',
    clientName: '롯데슈퍼 - 경남권',
    periodLabel: '2023년 06월',
  },
  {
    no: 30,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 제주권',
    periodLabel: '2023년 06월',
  },
  {
    no: 29,
    title: '냉동•냉장 저장고 전환공사',
    clientName: '(주)이마트 - 부산권',
    periodLabel: '2023년 06월',
  },
  {
    no: 28,
    title: '냉동•냉장 저장고 전환공사',
    clientName: '(주)이마트 - 경기 동부권',
    periodLabel: '2023년 06월',
  },
  {
    no: 27,
    title: '냉장고 문달기 에너지효율화 사업',
    clientName: '켑코이에스(주)',
    periodLabel: '2023년 06월',
  },
  {
    no: 26,
    title: '기계설비 유니트 쿨러 설치공사',
    clientName: '(주)이마트 - 부산권',
    periodLabel: '2023년 05월',
  },
  {
    no: 25,
    title: '에너지 합리화 사업 냉동기(콤프) 설계 및 시공',
    clientName: '(주)이마트 - 부산권',
    periodLabel: '2023년 05월',
  },
  {
    no: 24,
    title: '에너지 합리화 사업 냉동기(콤프) 설계 및 시공',
    clientName: '(주)이마트 - 강원권',
    periodLabel: '2023년 04월',
  },
  {
    no: 23,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 경기 동부권',
    periodLabel: '2023년 04월',
  },
  {
    no: 22,
    title: '냉장고 문달기 에너지효율화 사업',
    clientName: '켑코이에스(주)',
    periodLabel: '2023년 04월',
  },
  {
    no: 21,
    title: '에스코 냉장고 문달기 설계 및 설치공사',
    clientName: '롯데쇼핑(주)',
    periodLabel: '2023년 04월',
  },
  {
    no: 20,
    title: '냉장고 문달기 에너지효율화 사업',
    clientName: '켑코이에스(주)',
    periodLabel: '2023년 04월',
  },
  {
    no: 19,
    title: '기계설비 냉동기(콤프) 및 유니트 쿨러 설치공사',
    clientName: '(주)이마트 - 경기 서북권',
    periodLabel: '2023년 03월',
  },
  {
    no: 18,
    title: '기계설비 냉동기(콤프) 및 유니트 쿨러 설치공사',
    clientName: '(주)이마트 - 경기 서북권',
    periodLabel: '2023년 03월',
  },
  {
    no: 17,
    title: '에너지 합리화 사업 냉동기(콤프) 설계 및 시공',
    clientName: '(주)이마트 - 경기 동북권',
    periodLabel: '2023년 03월',
  },
  {
    no: 16,
    title: '에너지 합리화 사업 냉동기(콤프) 설계 및 시공',
    clientName: '(주)이마트 - 경기 남부권',
    periodLabel: '2023년 03월',
  },
  {
    no: 15,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 서울 동남권',
    periodLabel: '2023년 03월',
  },
  {
    no: 14,
    title: '와인샵 설계 및 쇼케이스 설치공사',
    clientName: '(주)신세계엘앤비',
    periodLabel: '2023년 03월',
  },
  {
    no: 13,
    title: '기계설비 냉동기(콤프) 설치공사',
    clientName: '롯데마트 - 서울 서북권',
    periodLabel: '2023년 03월',
  },
  {
    no: 12,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 서울 동북권',
    periodLabel: '2023년 03월',
  },
  {
    no: 11,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 서울 동남권',
    periodLabel: '2023년 02월',
  },
  {
    no: 10,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 전남권',
    periodLabel: '2023년 02월',
  },
  {
    no: 9,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 서울 동남권',
    periodLabel: '2023년 02월',
  },
  {
    no: 8,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 강원권',
    periodLabel: '2023년 02월',
  },
  {
    no: 7,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 서울 도심권',
    periodLabel: '2023년 02월',
  },
  {
    no: 6,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)부방유통 이마트 안양점',
    periodLabel: '2023년 02월',
  },
  {
    no: 5,
    title: '광주 평동 물류센터 산업용 유니트 쿨러 납품',
    clientName: '(유)한국공조시스템',
    periodLabel: '2023년 02월',
  },
  {
    no: 4,
    title: '기계설비 싱글콤프 멀티형 전환공사',
    clientName: '(주)이마트 - 서울 서남권',
    periodLabel: '2023년 02월',
  },
  {
    no: 3,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 대구권',
    periodLabel: '2023년 01월',
  },
  {
    no: 2,
    title: '냉동•냉장 장애처리 서비스 및 유지보수',
    clientName: '홈플러스(주)',
    periodLabel: '2023년 01월',
  },
  {
    no: 1,
    title: '와인샵 시설장비 유지관리 및 보수공사',
    clientName: '(주)신세계엘앤비',
    periodLabel: '2023년 01월~ 03월',
  },
  {
    no: 90,
    title: '쇼케이스 냉장고 문달기',
    clientName: '롯데프레시 - 충북권',
    periodLabel: '2024년 12월',
  },
  {
    no: 89,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경기남부권',
    periodLabel: '2024년 12월',
  },
  {
    no: 88,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 경북권',
    periodLabel: '2024년 12월',
  },
  {
    no: 87,
    title: '쇼케이스 리폼공사',
    clientName: '롯데쇼핑(주)- 경기남부권',
    periodLabel: '2024년 12월',
  },
  {
    no: 86,
    title: '쇼케이스 리폼공사',
    clientName: '롯데쇼핑(주)- 경기북부권',
    periodLabel: '2024년 12월',
  },
  {
    no: 85,
    title: '기계설비 인버터 냉동기 설치공사',
    clientName: '롯데쇼핑(주) - 강원권',
    periodLabel: '2024년 12월',
  },
  {
    no: 84,
    title: '쇼케이스 리폼공사',
    clientName: '롯데쇼핑(주) - 서울권',
    periodLabel: '2024년 12월',
  },
  {
    no: 83,
    title: '쇼케이스 리폼공사',
    clientName: '롯데쇼핑(주) - 서울권',
    periodLabel: '2024년 12월',
  },
  {
    no: 82,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 충남권',
    periodLabel: '2024년 12월',
  },
  {
    no: 81,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트- 경기남부권',
    periodLabel: '2024년 12월',
  },
  {
    no: 80,
    title: '쇼케이스 리폼공사',
    clientName: '롯데쇼핑(주)- 경기남부권',
    periodLabel: '2024년 12월',
  },
  {
    no: 79,
    title: '저장고 신설공사',
    clientName: '롯데쇼핑(주) - 서울권',
    periodLabel: '2024년 12월',
  },
  {
    no: 78,
    title: '쇼케이스 냉장고 문달기',
    clientName: '롯데쇼핑(주)- 경기북부권',
    periodLabel: '2024년 12월',
  },
  {
    no: 77,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 서울권',
    periodLabel: '2024년 11월',
  },
  {
    no: 76,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 강원권',
    periodLabel: '2024년 11월',
  },
  {
    no: 75,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 강원권',
    periodLabel: '2024년 11월',
  },
  {
    no: 74,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 경기북부권',
    periodLabel: '2024년 11월',
  },
  {
    no: 73,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 강원권',
    periodLabel: '2024년 11월',
  },
  {
    no: 72,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 강원권',
    periodLabel: '2024년 11월',
  },
  {
    no: 71,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 서울권',
    periodLabel: '2024년 11월',
  },
  {
    no: 70,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)풀무원',
    periodLabel: '2024년 11월',
  },
  {
    no: 69,
    title: '쇼케이스 냉장고 문달기',
    clientName: '롯데쇼핑(주)- 서울권',
    periodLabel: '2024년 10월',
  },
  {
    no: 68,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 충남권',
    periodLabel: '2024년 10월',
  },
  {
    no: 67,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 충남권',
    periodLabel: '2024년 10월',
  },
  {
    no: 66,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경기남부권',
    periodLabel: '2024년 10월',
  },
  {
    no: 65,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 강원권',
    periodLabel: '2024년 10월',
  },
  {
    no: 64,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 강원권',
    periodLabel: '2024년 10월',
  },
  {
    no: 63,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 강원권',
    periodLabel: '2024년 10월',
  },
  {
    no: 62,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 강원권',
    periodLabel: '2024년 10월',
  },
  {
    no: 61,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 강원권',
    periodLabel: '2024년 10월',
  },
  {
    no: 60,
    title: '쇼케이스 냉장고 문달기 에너지효율화 사업',
    clientName: '켑코이에스(주)',
    periodLabel: '2024년 10월',
  },
  {
    no: 59,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 경남권',
    periodLabel: '2024년 10월',
  },
  {
    no: 58,
    title: '쇼케이스 냉장고 문달기',
    clientName: '롯데쇼핑(주) - 경남권',
    periodLabel: '2024년 10월',
  },
  {
    no: 57,
    title: '쇼케이스 냉장고 문달기',
    clientName: '롯데쇼핑(주)- 충북권',
    periodLabel: '2024년 10월',
  },
  {
    no: 56,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 서울권',
    periodLabel: '2024년 10월',
  },
  {
    no: 55,
    title: '쇼케이스 냉장고 문달기',
    clientName: '롯데쇼핑(주) - 베트남',
    periodLabel: '2024년 10월',
  },
  {
    no: 54,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 강원권',
    periodLabel: '2024년 09월',
  },
  {
    no: 53,
    title: '쇼케이스 냉장고 문달기',
    clientName: '롯데쇼핑(주)- 강원권',
    periodLabel: '2024년 09월',
  },
  {
    no: 52,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 서울권',
    periodLabel: '2024년 09월',
  },
  {
    no: 51,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 서울권',
    periodLabel: '2024년 09월',
  },
  {
    no: 50,
    title: '동기조상기열교환기-Dry Cooler 납품',
    clientName: '한국전력공사',
    periodLabel: '2024년 09월',
  },
  {
    no: 49,
    title: '쇼케이스 철거공사',
    clientName: '(주)이마트 - 경기남부권',
    periodLabel: '2024년 09월',
  },
  {
    no: 48,
    title: '기계설비 유니트 쿨러 설치공사',
    clientName: '(주)이마트 - 서울권',
    periodLabel: '2024년 09월',
  },
  {
    no: 47,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)풀무원',
    periodLabel: '2024년 09월',
  },
  {
    no: 46,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경남권',
    periodLabel: '2024년 09월',
  },
  {
    no: 45,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 경북권',
    periodLabel: '2024년 08월',
  },
  {
    no: 44,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경기남부권',
    periodLabel: '2024년 08월',
  },
  {
    no: 43,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주)- 전북권',
    periodLabel: '2024년 08월',
  },
  {
    no: 42,
    title: '쇼케이스 냉장고 문달기',
    clientName: '(주)이마트- 경기남부권',
    periodLabel: '2024년 08월',
  },
  {
    no: 41,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)신세계푸드',
    periodLabel: '2024년 08월',
  },
  {
    no: 40,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 서울권',
    periodLabel: '2024년 08월',
  },
  {
    no: 39,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 경남권',
    periodLabel: '2024년 08월',
  },
  {
    no: 38,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경남권',
    periodLabel: '2024년 08월',
  },
  {
    no: 37,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 경남권',
    periodLabel: '2024년 08월',
  },
  {
    no: 36,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경기남부권',
    periodLabel: '2024년 08월',
  },
  {
    no: 35,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)풀무원',
    periodLabel: '2024년 08월',
  },
  {
    no: 34,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경기남부권',
    periodLabel: '2024년 08월',
  },
  {
    no: 33,
    title: '환경개선 설계 및 개선공사',
    clientName: '쿠팡풀필먼트서비스(유)',
    periodLabel: '2024년 08월',
  },
  {
    no: 32,
    title: '기계설비 유니트 쿨러 설치공사',
    clientName: '쿠팡풀필먼트서비스(유)',
    periodLabel: '2024년 08월',
  },
  {
    no: 31,
    title: '쇼케이스 교체공사',
    clientName: '롯데쇼핑(주)- 경기남부권',
    periodLabel: '2024년 07월',
  },
  {
    no: 30,
    title: '쇼케이스 냉장고 문달기',
    clientName: '농협하나로마트 - 경남권',
    periodLabel: '2024년 07월',
  },
  {
    no: 29,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 서울권',
    periodLabel: '2024년 07월',
  },
  {
    no: 28,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트- 경기남부권',
    periodLabel: '2024년 07월',
  },
  {
    no: 27,
    title: '쇼케이스 냉장고 문달기',
    clientName: '롯데백화점 - 서울권',
    periodLabel: '2024년 07월',
  },
  {
    no: 26,
    title: '쇼케이스 냉장고 문달기',
    clientName: '롯데쇼핑(주) - 서울권',
    periodLabel: '2024년 07월',
  },
  {
    no: 25,
    title: '기계설비 냉동기(콤프) 보수공사',
    clientName: '(주)신세계푸드',
    periodLabel: '2024년 06월',
  },
  {
    no: 24,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 전북권',
    periodLabel: '2024년 06월',
  },
  {
    no: 23,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 강원권',
    periodLabel: '2024년 06월',
  },
  {
    no: 22,
    title: '쇼케이스 이설공사',
    clientName: '(주)이마트 - 충북권',
    periodLabel: '2024년 06월',
  },
  {
    no: 21,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 경북권',
    periodLabel: '2024년 06월',
  },
  {
    no: 20,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경기남부권',
    periodLabel: '2024년 06월',
  },
  {
    no: 19,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 경북권',
    periodLabel: '2024년 06월',
  },
  {
    no: 18,
    title: '쇼케이스 냉장고 문달기',
    clientName: '(주)이마트 - 경기남부권',
    periodLabel: '2024년 06월',
  },
  {
    no: 17,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 서울권',
    periodLabel: '2024년 06월',
  },
  {
    no: 16,
    title: '에너지합리화사업 친환경 S/C COMP 교체 공사',
    clientName: '(주)이마트 - 경남권',
    periodLabel: '2024년 05월',
  },
  {
    no: 15,
    title: '에너지합리화사업 친환경 S/C COMP 교체 공사',
    clientName: '(주)이마트 - 경남권',
    periodLabel: '2024년 05월',
  },
  {
    no: 14,
    title: '기계설비 냉동기(콤프) 및 유니트 쿨러 설치공사',
    clientName: '(주)풀무원',
    periodLabel: '2024년 05월',
  },
  {
    no: 13,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경남권',
    periodLabel: '2024년 05월',
  },
  {
    no: 12,
    title: '냉매 회수 및 폐기 처리',
    clientName: '(주)이마트 - 서울권',
    periodLabel: '2024년 05월',
  },
  {
    no: 11,
    title: '기계설비 배관 설계 및 설치공사',
    clientName: '롯데쇼핑(주) 롯데신선품질혁신센터',
    periodLabel: '2024년 05월',
  },
  {
    no: 10,
    title: '쇼케이스 냉장고 문달기',
    clientName: '이마트24',
    periodLabel: '2024년 05월',
  },
  {
    no: 9,
    title: '기계설비 싱글콤프 멀티형 설계 및 전환공사',
    clientName: '(주)이마트 - 충북권',
    periodLabel: '2024년 04월',
  },
  {
    no: 8,
    title: '쇼케이스 설계 및 리뉴얼 설치공사',
    clientName: '롯데쇼핑(주) - 서울권',
    periodLabel: '2024년 04월',
  },
  {
    no: 7,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 경기남부권',
    periodLabel: '2024년 04월',
  },
  {
    no: 6,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 경남권',
    periodLabel: '2024년 04월',
  },
  {
    no: 5,
    title: '에너지합리화사업 친환경 S/C COMP 교체 공사',
    clientName: '(주)이마트 - 경기권',
    periodLabel: '2024년 04월',
  },
  {
    no: 4,
    title: '기계설비 유니트 쿨러 교체공사',
    clientName: '(주)이마트 - 경남권',
    periodLabel: '2024년 04월',
  },
  {
    no: 3,
    title: '기계설비 냉동기(콤프) 교체공사',
    clientName: '(주)이마트 - 경남권',
    periodLabel: '2024년 04월',
  },
  {
    no: 2,
    title: '에너지합리화사업 친환경 S/C COMP 교체 공사',
    clientName: '(주)이마트 - 강원권',
    periodLabel: '2024년 03월',
  },
  {
    no: 1,
    title: '기계설비 냉동기(콤프) 개선공사',
    clientName: '(주)신세계푸드',
    periodLabel: '2024년 03월',
  },
];

let mockProjectRecords: MockProjectRecord[] = (() => {
  const monthOrderMap = new Map<string, number>();

  return projectRecordSeeds.map((seed, index) => {
    const periodData = parseProjectRecordPeriodLabel(seed.periodLabel);
    const periodKey = periodData
      ? `${String(periodData.year)}-${String(periodData.month).padStart(2, '0')}`
      : seed.periodLabel;
    const monthOrder = (monthOrderMap.get(periodKey) ?? 0) + 1;
    monthOrderMap.set(periodKey, monthOrder);

    const period = createProjectRecordPeriod(seed.periodLabel, monthOrder);
    const thumbnail = projectRecordThumbnailPool[index % projectRecordThumbnailPool.length];
    const seedYear = periodData?.year ?? 2022;

    return {
      id: seedYear * 1000 + seed.no,
      title: seed.title,
      summary: `${seed.clientName} 현장에서 수행한 ${seed.title} 공사실적입니다.`,
      category: inferProjectRecordCategory(seed.clientName),
      clientName: seed.clientName,
      overview:
        `${seed.clientName} 현장에서 수행한 ${seed.title} 공사실적입니다.\n` +
        `공사기간은 ${seed.periodLabel} 기준으로 정리했습니다.`,
      scopeItems: [`${seed.title} 수행`, `${seed.clientName} 현장 대응`, '공정 및 유지관리 점검'],
      featurePoints: [
        {
          title: 'POINT 1',
          description: '공정 계획과 설치 범위를 현장 조건에 맞춰 조정',
        },
        {
          title: 'POINT 2',
          description: '운영 조건을 반영한 시공 및 유지관리 대응',
        },
        {
          title: 'POINT 3',
          description: '시운전과 후속 점검 기준까지 함께 정리',
        },
      ],
      thumbnailFileUrl: thumbnail.fileUrl,
      thumbnailFileName: thumbnail.fileName,
      thumbnailContentType: 'image/jpeg',
      thumbnailFileSize: 120_000,
      galleryImages: [],
      content:
        `<p>${seed.clientName} 현장에서 수행한 ${seed.title} 공사실적입니다.</p>` +
        `<p>공사기간: ${seed.periodLabel}</p>`,
      isPublished: true,
      viewCount: 80 + seed.no,
      ...period,
    };
  });
})();

const supportMaterialMockEntries = [
  ['저압필터 - 내부 구조 및 기능', '교육자료', '2021-07-21T09:00:00'],
  ['액분리기 - 냉매액 분리 구조', '교육자료', '2021-07-21T08:59:00'],
  ['쇼케이스 - 냉기 순환 구조', '교육자료', '2021-07-21T08:58:00'],
  ['팽창변의 원리 - 작동 구조', '교육자료', '2021-07-21T08:57:00'],
  ['필터 드라이어 - 내부 구조 및 기능', '교육자료', '2021-07-21T08:56:00'],
  ['수냉식 응축기 - 응축 구조', '교육자료', '2021-07-21T08:55:00'],
  ['유분리기 - 냉동 오일 분리 및 회수 구조', '교육자료', '2021-07-21T08:54:00'],
  ['압축기 - 냉동 오일의 순환 구조', '교육자료', '2021-07-21T08:53:00'],
  ['압축기 - 피스톤의 구조 (측면)', '교육자료', '2021-07-21T08:52:00'],
  ['압축기 - 피스톤의 구조 (정면)', '교육자료', '2021-07-21T08:51:00'],
  ['냉동 장비의 구성 및 냉매 흐름도', '교육자료', '2021-07-21T08:50:00'],
  ['수냉식(냉동,냉장) 세미멀티 유니트 설계 도면', '교육자료', '2018-09-05T09:00:00'],
  ['압축기(콤프) 내부 부품 이해 및 이상 발생시 조치 사항', '교육자료', '2018-08-18T09:00:00'],
  ['냉동 장비 기초 교육', '교육자료', '2018-08-18T08:59:00'],
  ['압축기(콤프) 분해 조립 및 이상 발생시 조치 사항', '교육자료', '2016-08-05T09:00:00'],
  ['냉동기 과열도 측정 방법', '기술자료', '2015-09-04T09:00:00'],
  ['ECP 설명서', '기술자료', '2015-09-02T09:00:00'],
  ['3방 밸브 설명서', '기술자료', '2015-09-02T08:59:00'],
  ['HPS 회로 구성', '기술자료', '2015-09-02T08:58:00'],
  ['EMPR 회로 구성', '기술자료', '2015-08-31T09:00:00'],
  ['DPS 회로 구성', '기술자료', '2015-08-31T08:59:00'],
  ['OPS 취급 메뉴얼', '기술자료', '2015-08-31T08:58:00'],
  ['OPS 회로 구성', '기술자료', '2015-08-31T08:57:00'],
  ['압축기 교체시 주변 부품 교체의 필요성', '교육자료', '2014-12-29T09:00:00'],
  ['압축기 교체시 부품 교체의 필요성', '교육자료', '2014-12-29T08:59:00'],
  ['저압 코이쉘 필터링 작업', '교육자료', '2014-04-17T09:00:00'],
  ['냉동기 분해 점검', '교육자료', '2014-04-15T09:00:00'],
  ['냉동 장비의 고장 진단 및 대응 방법 (현장 실무)', '교육자료', '2013-12-17T09:00:00'],
  ['냉동 장비의 구성 (기초교육)', '교육자료', '2013-12-17T08:59:00'],
] as const;

const supportMaterialMockFiles = [
  {
    fileName: 'website-renewal-guide-260202.pdf',
    fileSize: 1_200_000,
    fileUrl: technicalGuidePdfUrl,
  },
  {
    fileName: 'kookje-tnc-catalog-v2-25-08-05.pdf',
    fileSize: 9_800_000,
    fileUrl: technicalCatalogPdfUrl,
  },
  {
    fileName: 'mechanical-facilities-brochure.pdf',
    fileSize: 3_200_000,
    fileUrl: technicalBrochurePdfUrl,
  },
] as const;

const supportMaterialMockSlugs = [
  '2e3b6cce-dc71-5876-9d88-fefeeaa18dd3',
  '7f4b507a-a0d6-5495-98cb-53cf64367339',
  '9ac20768-066b-5e1d-8862-d9854fb43bcd',
  'd0241c34-8b86-5c70-80ba-70f94bfec5df',
  'a8dbf040-950e-5d8d-808a-6b5abe3c2f3c',
  '1aec4d26-75a2-5df7-95c1-e11c7106b8a3',
  '1a0873d6-394d-59cd-992c-982ba25bc904',
  'b1d3206e-6e30-518b-a59c-83c1b34b1c57',
  'be7ae948-a701-5e3c-b013-1cd3d48f7acd',
  'a787e93c-f12e-57cb-ba67-c7680cf6a287',
  '2bf2f95f-6c01-5064-9008-d3583257cc6d',
  '329ff0aa-01f1-59d4-a076-85efbcdb08f1',
  'ae600b9f-0294-5263-b836-b5d9aee18834',
  'b51ff7ea-91f1-551d-8d8a-ec7eec95d26c',
  'b1891aa2-cd28-596e-bf6d-5100c5872247',
  'c15c79bb-4dfd-5f81-860d-25e381a6fc5e',
  '0c0344e0-e15a-52f7-98bd-f9e3d1a8a765',
  '826dc12d-5e0d-5038-88c1-e2d643200f5e',
  'd55ac4d4-a313-51fd-9d7a-b1ac271c2bf0',
  '142645aa-3200-5e04-8512-ed3dbaade799',
  '56142ad9-bf1c-5219-b031-045e7f112ae4',
  '87488a87-ccf7-544a-a66a-51168dd911fb',
  '56e616a7-2775-521e-9e03-79272670f550',
  '003705e1-92aa-551a-a1fb-5e9d96957b68',
  '666e0877-7371-578c-bae2-3085227f9f53',
  '3416831c-a59f-5a98-81f3-0bf369e80649',
  'bb893d2d-0f35-5f8b-9ca1-5f033f5c1516',
  '02be1b47-24ae-584d-87af-09183eee7f10',
  '67143b17-79f4-50f2-902c-b501520930ac',
] as const;

let mockSupportMaterialList: MockSupportMaterial[] = supportMaterialMockEntries.map(
  ([title, category, publishedAt], index) => {
    const file = supportMaterialMockFiles[index % supportMaterialMockFiles.length];
    const isEducationMaterial = isEducationMaterialCategory(category);
    const videoUrl = technicalEducationVideoUrls[index % technicalEducationVideoUrls.length] ?? '';

    return {
      id: index + 1,
      slug: supportMaterialMockSlugs[index] ?? crypto.randomUUID(),
      title,
      description: `${title} 관련 샘플 자료입니다.`,
      category,
      categoryCode: toSupportMaterialCategoryCode(category),
      assetKind: isEducationMaterial ? 'VIDEO' : 'FILE',
      fileName: isEducationMaterial ? '' : file.fileName,
      fileUrl: isEducationMaterial ? '' : file.fileUrl,
      fileSize: isEducationMaterial ? 0 : file.fileSize,
      videoUrl,
      videoProvider: isEducationMaterial ? 'YOUTUBE' : '',
      images: [],
      viewCount: 120 + index * 7,
      publishedAt,
      content: `${title} 샘플 본문입니다.\n\n개발 환경에서 기술자료 게시판과 페이지네이션을 확인하기 위한 예시 데이터입니다.`,
      contentType: isEducationMaterial ? 'text/html' : 'application/pdf',
      isPublished: true,
    };
  },
);

let mockInquiries: MockInquiry[] = [
  {
    id: 1,
    categoryCode: 1,
    categoryName: '에너지솔루션',
    companyName: '세원푸드',
    contactName: '김지훈',
    department: '설비구매팀',
    phone: '010-2222-1111',
    email: 'jhkim@sewonfood.co.kr',
    message:
      '신규 저온창고 증설 관련으로 에너지 절감형 냉동 시스템 제안을 요청드립니다. 4월 중 현장 미팅 가능 일정도 함께 확인 부탁드립니다.',
    agreePrivacy: true,
    agreeMarketing: false,
    status: 'RECEIVED',
    adminMemo: '',
    assignedAdminUsername: '',
    respondedAt: null,
    createdAt: '2026-04-11T09:20:00',
    updatedAt: '2026-04-11T09:20:00',
  },
  {
    id: 2,
    categoryCode: 2,
    categoryName: '기계·공조설비',
    companyName: '한울메디컬센터',
    contactName: '박소연',
    department: '시설관리실',
    phone: '010-4444-8855',
    email: 'sypark@hanulmc.com',
    message:
      '중앙 공조설비 교체 검토 중입니다. 기존 장비 스펙 기준으로 제안 가능 여부와 유지보수 범위를 알고 싶습니다.',
    agreePrivacy: true,
    agreeMarketing: true,
    status: 'IN_PROGRESS',
    adminMemo: '현장 도면 요청 메일 발송. 회신 대기 중.',
    assignedAdminUsername: 'admin',
    respondedAt: null,
    createdAt: '2026-04-10T14:10:00',
    updatedAt: '2026-04-10T16:40:00',
  },
  {
    id: 3,
    categoryCode: 3,
    categoryName: '냉장·냉동시스템',
    companyName: '동해수산',
    contactName: '이민재',
    department: '생산관리팀',
    phone: '010-9999-0021',
    email: 'mjlee@eastsea.co.kr',
    message:
      '수산물 가공 라인 증설과 함께 저온 저장 설비를 검토하고 있습니다. 예상 납기와 시공 가능 지역을 문의드립니다.',
    agreePrivacy: true,
    agreeMarketing: false,
    status: 'DONE',
    adminMemo: '1차 유선 상담 완료. 제안서 전달 후 후속 일정 협의.',
    assignedAdminUsername: 'manager',
    respondedAt: '2026-04-09T11:30:00',
    createdAt: '2026-04-08T08:45:00',
    updatedAt: '2026-04-09T11:30:00',
  },
];

const sortByPublishedAtDesc = <T extends { publishedAt: string }>(items: T[]): T[] => {
  return [...items].sort((left, right) => {
    return new Date(right.publishedAt).getTime() - new Date(left.publishedAt).getTime();
  });
};

const sortByCreatedAtDesc = <T extends { createdAt: string }>(items: T[]): T[] => {
  return [...items].sort((left, right) => {
    return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
  });
};

const toProjectRecordSummary = (projectRecord: MockProjectRecord): ProjectRecordSummary => {
  return {
    id: projectRecord.id,
    title: projectRecord.title,
    summary: projectRecord.summary,
    viewCount: projectRecord.viewCount,
    publishedAt: projectRecord.publishedAt,
    ...(projectRecord.category ? { category: projectRecord.category } : {}),
    ...(projectRecord.clientName ? { clientName: projectRecord.clientName } : {}),
    ...(projectRecord.constructionStartDate
      ? { constructionStartDate: projectRecord.constructionStartDate }
      : {}),
    ...(projectRecord.constructionEndDate
      ? { constructionEndDate: projectRecord.constructionEndDate }
      : {}),
    ...(projectRecord.thumbnailFileUrl ? { thumbnailFileUrl: projectRecord.thumbnailFileUrl } : {}),
    ...(projectRecord.translations
      ? { translations: projectRecord.translations.map((translation) => ({ ...translation })) }
      : {}),
  };
};

const toAdminProjectRecordSummary = (
  projectRecord: MockProjectRecord,
): AdminProjectRecordSummary => {
  return {
    ...toProjectRecordSummary(projectRecord),
    isPublished: projectRecord.isPublished,
  };
};

const toSupportMaterialSummary = (supportMaterial: MockSupportMaterial): SupportMaterialSummary => {
  return {
    id: supportMaterial.id,
    slug: supportMaterial.slug,
    title: supportMaterial.title,
    description: supportMaterial.description,
    category: supportMaterial.category,
    categoryCode: supportMaterial.categoryCode,
    assetKind: supportMaterial.assetKind,
    fileName: supportMaterial.fileName,
    fileUrl: supportMaterial.fileUrl,
    fileSize: supportMaterial.fileSize,
    videoUrl: supportMaterial.videoUrl,
    videoProvider: supportMaterial.videoProvider,
    viewCount: supportMaterial.viewCount,
    publishedAt: supportMaterial.publishedAt,
  };
};

const toAdminSupportMaterialSummary = (
  supportMaterial: MockSupportMaterial,
): AdminSupportMaterialSummary => {
  return {
    ...toSupportMaterialSummary(supportMaterial),
    isPublished: supportMaterial.isPublished,
  };
};

const toAdminInquirySummary = (inquiry: MockInquiry): AdminInquirySummary => {
  return {
    id: inquiry.id,
    categoryCode: inquiry.categoryCode,
    categoryName: inquiry.categoryName,
    companyName: inquiry.companyName,
    contactName: inquiry.contactName,
    phone: inquiry.phone,
    email: inquiry.email,
    status: inquiry.status,
    createdAt: inquiry.createdAt,
  };
};

const toPopupSummary = (popup: MockPopup): PopupSummary => {
  return {
    id: popup.id,
    title: popup.title,
    summary: popup.summary,
    linkUrl: popup.linkUrl,
    imageUrl: popup.imageUrl,
    imageFit: popup.imageFit,
    imageObjectPosition: popup.imageObjectPosition,
    imageScale: popup.imageScale,
    isPinned: popup.isPinned,
    publishedAt: popup.publishedAt,
  };
};

const toAdminPopupSummary = (popup: MockPopup): AdminPopupSummary => {
  return {
    ...toPopupSummary(popup),
    isPublished: popup.isPublished,
  };
};

const toConstructionCaseSummary = (
  constructionCase: MockConstructionCase,
): ConstructionCaseSummary => {
  return {
    id: constructionCase.id,
    slug: constructionCase.slug,
    title: constructionCase.title,
    summary: constructionCase.summary,
    ...(constructionCase.category ? { category: constructionCase.category } : {}),
    ...(constructionCase.clientName ? { clientName: constructionCase.clientName } : {}),
    thumbnailFileUrl: constructionCase.thumbnailFileUrl,
    thumbnailFit: constructionCase.thumbnailFit,
    thumbnailObjectPosition: constructionCase.thumbnailObjectPosition,
    thumbnailScale: constructionCase.thumbnailScale,
    isFeatured: constructionCase.isFeatured,
    publishedAt: constructionCase.publishedAt,
  };
};

const toAdminConstructionCaseSummary = (
  constructionCase: MockConstructionCase,
): AdminConstructionCaseSummary => {
  return {
    ...toConstructionCaseSummary(constructionCase),
    viewCount: constructionCase.viewCount,
    isPublished: constructionCase.isPublished,
  };
};

const cloneProjectRecordDetail = (projectRecord: MockProjectRecord): ProjectRecordDetail => {
  return {
    ...projectRecord,
    translations: projectRecord.translations?.map((translation) => ({ ...translation })) ?? [],
    ...(projectRecord.galleryImages
      ? {
          galleryImages: projectRecord.galleryImages.map((image) => ({ ...image })),
        }
      : {}),
  };
};

const cloneSupportMaterialDetail = (
  supportMaterial: MockSupportMaterial,
): SupportMaterialDetail => {
  return {
    ...supportMaterial,
    images: supportMaterial.images.map((image) => ({ ...image })),
  };
};

const clonePopupDetail = (popup: MockPopup): PopupDetail => {
  return { ...popup };
};

const createMockPopupImage = (input: PopupMutationInput, current?: MockPopup) => {
  if (!input.image && current) {
    return {
      imageContentType: current.imageContentType,
      imageFileName: current.imageFileName,
      imageFileSize: current.imageFileSize,
      imageUrl: current.imageUrl,
    };
  }

  if (!input.image) {
    throw new Error('팝업 이미지는 필수입니다.');
  }

  return {
    imageContentType: input.image.type || 'image/jpeg',
    imageFileName: input.image.name,
    imageFileSize: input.image.size,
    imageUrl: URL.createObjectURL(input.image),
  };
};

const cloneConstructionCaseDetail = (
  constructionCase: MockConstructionCase,
): ConstructionCaseDetail => {
  return {
    ...constructionCase,
    galleryImages: constructionCase.galleryImages.map((image) => ({ ...image })),
  };
};

const getNextMockId = (items: Array<{ id: number }>) => {
  return Math.max(0, ...items.map((item) => item.id)) + 1;
};

const createMockSlug = (title: string, exists: (slug: string) => boolean, fallback = 'item') => {
  const normalizedSlug = title
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
  const baseSlug = normalizedSlug || `${fallback}-${Math.random().toString(16).slice(2, 10)}`;

  if (!exists(baseSlug)) {
    return baseSlug;
  }

  let suffix = 2;
  while (exists(`${baseSlug}-${String(suffix)}`)) {
    suffix += 1;
  }

  return `${baseSlug}-${String(suffix)}`;
};

const revokeMockFileUrl = (fileUrl: string) => {
  if (!fileUrl.startsWith('blob:')) {
    return;
  }

  URL.revokeObjectURL(fileUrl);
};

const createMockSupportMaterialAsset = (
  input: Pick<SupportMaterialMutationInput, 'assetKind' | 'file' | 'imageFiles' | 'videoUrl'>,
  current?: MockSupportMaterial,
) => {
  if (input.assetKind === 'VIDEO') {
    const nextVideoUrl = input.videoUrl.trim() || current?.videoUrl || '';

    if (!nextVideoUrl) {
      throw new Error('YouTube 영상 URL이 필요합니다.');
    }

    if (current?.assetKind === 'FILE') {
      revokeMockFileUrl(current.fileUrl);
    }
    if (current?.assetKind === 'IMAGE') {
      current.images.forEach((image) => {
        revokeMockFileUrl(image.fileUrl);
      });
    }

    return {
      assetKind: 'VIDEO' as const,
      contentType: 'text/html',
      fileName: '',
      fileSize: 0,
      fileUrl: '',
      images: [],
      videoProvider: 'YOUTUBE',
      videoUrl: nextVideoUrl,
    };
  }

  if (input.assetKind === 'IMAGE') {
    if (input.imageFiles.length === 0 && current?.assetKind === 'IMAGE') {
      return {
        assetKind: 'IMAGE' as const,
        contentType: '',
        fileName: '',
        fileSize: 0,
        fileUrl: '',
        images: current.images.map((image) => ({ ...image })),
        videoProvider: '',
        videoUrl: '',
      };
    }

    if (input.imageFiles.length === 0) {
      throw new Error('이미지 파일이 필요합니다.');
    }

    if (current?.assetKind === 'FILE') {
      revokeMockFileUrl(current.fileUrl);
    }
    if (current?.assetKind === 'IMAGE') {
      current.images.forEach((image) => {
        revokeMockFileUrl(image.fileUrl);
      });
    }

    return {
      assetKind: 'IMAGE' as const,
      contentType: '',
      fileName: '',
      fileSize: 0,
      fileUrl: '',
      images: createMockSupportMaterialImages(input.imageFiles),
      videoProvider: '',
      videoUrl: '',
    };
  }

  if (!input.file && current?.assetKind === 'FILE') {
    return {
      assetKind: 'FILE' as const,
      contentType: current.contentType,
      fileName: current.fileName,
      fileSize: current.fileSize,
      fileUrl: current.fileUrl,
      images: [],
      videoProvider: '',
      videoUrl: '',
    };
  }

  if (!input.file) {
    throw new Error('기술자료 파일이 필요합니다.');
  }

  if (current?.assetKind === 'IMAGE') {
    current.images.forEach((image) => {
      revokeMockFileUrl(image.fileUrl);
    });
  }

  return {
    assetKind: 'FILE' as const,
    contentType: input.file.type || 'application/octet-stream',
    fileName: input.file.name,
    fileSize: input.file.size,
    fileUrl: URL.createObjectURL(input.file),
    images: [],
    videoProvider: '',
    videoUrl: '',
  };
};

const createMockSupportMaterialImages = (files: File[]): SupportMaterialImage[] => {
  return files.map((file, index) => ({
    id: Date.now() + index,
    displayOrder: index + 1,
    fileName: file.name,
    fileUrl: URL.createObjectURL(file),
    contentType: file.type || 'image/jpeg',
    fileSize: file.size,
  }));
};

const revokeConstructionCaseImages = (constructionCase: MockConstructionCase) => {
  revokeMockFileUrl(constructionCase.thumbnailFileUrl);
  constructionCase.galleryImages.forEach((image) => {
    revokeMockFileUrl(image.fileUrl);
  });
};

const createMockConstructionCaseFiles = (
  input: ConstructionCaseMutationInput,
  current?: MockConstructionCase,
) => {
  const thumbnail = input.thumbnail
    ? {
        thumbnailFileName: input.thumbnail.name,
        thumbnailFileUrl: URL.createObjectURL(input.thumbnail),
        thumbnailContentType: input.thumbnail.type || 'image/jpeg',
        thumbnailFileSize: input.thumbnail.size,
      }
    : current
      ? {
          thumbnailFileName: current.thumbnailFileName,
          thumbnailFileUrl: current.thumbnailFileUrl,
          thumbnailContentType: current.thumbnailContentType,
          thumbnailFileSize: current.thumbnailFileSize,
        }
      : null;

  if (!thumbnail) {
    throw new Error('대표 썸네일이 필요합니다.');
  }

  const hasGallerySlots = input.galleryImageSlots.length > 0;
  const currentImagesById = new Map(current?.galleryImages.map((image) => [image.id, image]));
  const galleryImages = hasGallerySlots
    ? input.galleryImageSlots.flatMap((slot, index) => {
        const placement =
          input.galleryImagePlacements[index] ?? defaultConstructionCaseImagePlacement;
        if (slot.startsWith('existing:')) {
          const currentImage = currentImagesById.get(Number(slot.replace('existing:', '')));
          return currentImage
            ? [
                {
                  ...currentImage,
                  displayOrder: index + 1,
                  imageFit: placement.imageFit,
                  imageObjectPosition: placement.imageObjectPosition,
                  imageScale: placement.imageScale,
                },
              ]
            : [];
        }

        const fileIndex = Number(slot.replace('new:', ''));
        if (
          !Number.isInteger(fileIndex) ||
          fileIndex < 0 ||
          fileIndex >= input.galleryImages.length
        ) {
          return [];
        }

        const file = input.galleryImages[fileIndex];
        return [
          {
            id: Date.now() + index,
            displayOrder: index + 1,
            fileName: file.name,
            fileUrl: URL.createObjectURL(file),
            contentType: file.type || 'image/jpeg',
            fileSize: file.size,
            imageFit: placement.imageFit,
            imageObjectPosition: placement.imageObjectPosition,
            imageScale: placement.imageScale,
          },
        ];
      })
    : input.galleryImages.length > 0
      ? input.galleryImages.map((file, index) => {
          const placement =
            input.galleryImagePlacements[index] ?? defaultConstructionCaseImagePlacement;

          return {
            id: Date.now() + index,
            displayOrder: index + 1,
            fileName: file.name,
            fileUrl: URL.createObjectURL(file),
            contentType: file.type || 'image/jpeg',
            fileSize: file.size,
            imageFit: placement.imageFit,
            imageObjectPosition: placement.imageObjectPosition,
            imageScale: placement.imageScale,
          };
        })
      : input.removeGallery
        ? []
        : current
          ? current.galleryImages.map((image) => {
              const placement = input.existingGalleryImagePlacements.find(
                (item) => item.id === image.id,
              );

              return {
                ...image,
                ...(placement
                  ? {
                      imageFit: placement.imageFit,
                      imageObjectPosition: placement.imageObjectPosition,
                      imageScale: placement.imageScale,
                    }
                  : {}),
              };
            })
          : [];

  return {
    ...thumbnail,
    thumbnailFit: input.thumbnailFit,
    thumbnailObjectPosition: input.thumbnailObjectPosition,
    thumbnailScale: input.thumbnailScale,
    galleryImages,
  };
};

const mockFetchAdminPopups = (): Promise<AdminPopupSummary[]> => {
  return resolveMock(sortByPublishedAtDesc(mockPopups).map(toAdminPopupSummary));
};

const mockFetchAdminPopup = (popupId: number): Promise<PopupDetail> => {
  const popup = mockPopups.find((item) => item.id === popupId);

  if (!popup) {
    return rejectMock('팝업을 찾지 못했습니다.');
  }

  return resolveMock(clonePopupDetail(popup));
};

const mockCreatePopup = (input: PopupMutationInput): Promise<PopupDetail> => {
  if (input.isPublished && mockPopups.filter((popup) => popup.isPublished).length >= 3) {
    return rejectMock('공개 팝업은 최대 3개까지 등록할 수 있습니다.');
  }

  const image = createMockPopupImage(input);
  const popup: MockPopup = {
    id: getNextMockId(mockPopups),
    title: input.title,
    summary: input.title,
    content: '',
    linkUrl: input.linkUrl,
    imageFit: input.imageFit,
    imageObjectPosition: input.imageObjectPosition,
    imageScale: input.imageScale,
    isPinned: false,
    isPublished: input.isPublished,
    publishedAt: input.publishedAt,
    ...image,
  };

  mockPopups = [popup, ...mockPopups];

  return resolveMock(clonePopupDetail(popup));
};

const mockUpdatePopup = (popupId: number, input: PopupMutationInput): Promise<PopupDetail> => {
  const currentPopup = mockPopups.find((item) => item.id === popupId);

  if (!currentPopup) {
    return rejectMock('팝업을 찾지 못했습니다.');
  }

  if (
    input.isPublished &&
    mockPopups.filter((popup) => popup.isPublished && popup.id !== popupId).length >= 3
  ) {
    return rejectMock('공개 팝업은 최대 3개까지 등록할 수 있습니다.');
  }

  const image = createMockPopupImage(input, currentPopup);
  const updatedPopup: MockPopup = {
    ...currentPopup,
    title: input.title,
    summary: input.title,
    content: '',
    linkUrl: input.linkUrl,
    imageFit: input.imageFit,
    imageObjectPosition: input.imageObjectPosition,
    imageScale: input.imageScale,
    isPinned: false,
    isPublished: input.isPublished,
    publishedAt: input.publishedAt,
    ...image,
  };

  mockPopups = mockPopups.map((item) => (item.id === popupId ? updatedPopup : item));

  return resolveMock(clonePopupDetail(updatedPopup));
};

const mockDeletePopup = (popupId: number): Promise<void> => {
  mockPopups = mockPopups.filter((item) => item.id !== popupId);
  return Promise.resolve();
};

const mockFetchConstructionCases = (): Promise<ConstructionCaseSummary[]> => {
  return resolveMock(
    sortByPublishedAtDesc(mockConstructionCases)
      .filter((constructionCase) => constructionCase.isPublished)
      .map(toConstructionCaseSummary),
  );
};

const mockFetchConstructionCase = (
  constructionCaseId: number | string,
): Promise<ConstructionCaseDetail> => {
  const constructionCaseKey = String(constructionCaseId);
  const constructionCase = mockConstructionCases.find(
    (item) =>
      item.isPublished &&
      (String(item.id) === constructionCaseKey || item.slug === constructionCaseKey),
  );

  if (!constructionCase) {
    return rejectMock('시공사례를 찾지 못했습니다.');
  }

  constructionCase.viewCount += 1;

  return resolveMock(cloneConstructionCaseDetail(constructionCase));
};

const mockFetchAdminConstructionCases = (): Promise<AdminConstructionCaseSummary[]> => {
  return resolveMock(
    sortByPublishedAtDesc(mockConstructionCases).map(toAdminConstructionCaseSummary),
  );
};

const mockFetchAdminConstructionCase = (
  constructionCaseId: number,
): Promise<ConstructionCaseDetail> => {
  const constructionCase = mockConstructionCases.find((item) => item.id === constructionCaseId);

  if (!constructionCase) {
    return rejectMock('시공사례를 찾지 못했습니다.');
  }

  return resolveMock(cloneConstructionCaseDetail(constructionCase));
};

const mockCreateConstructionCase = (
  input: ConstructionCaseMutationInput,
): Promise<ConstructionCaseDetail> => {
  const files = createMockConstructionCaseFiles(input);
  const constructionCase: MockConstructionCase = {
    id: getNextMockId(mockConstructionCases),
    slug: createMockSlug(
      input.title,
      (slug) => mockConstructionCases.some((item) => item.slug === slug),
      'construction-case',
    ),
    title: input.title,
    summary: input.summary,
    content: input.content,
    category: input.category,
    clientName: input.clientName,
    constructionStartDate: input.constructionStartDate,
    constructionEndDate: input.constructionEndDate,
    location: input.location,
    overview: input.overview,
    scopeItems: input.scopeItems,
    featurePoints: input.featurePoints,
    isFeatured: input.isFeatured,
    isPublished: input.isPublished,
    publishedAt: input.publishedAt,
    viewCount: 0,
    ...files,
  };

  mockConstructionCases = [constructionCase, ...mockConstructionCases];

  return resolveMock(cloneConstructionCaseDetail(constructionCase));
};

const mockUpdateConstructionCase = (
  constructionCaseId: number,
  input: ConstructionCaseMutationInput,
): Promise<ConstructionCaseDetail> => {
  const currentConstructionCase = mockConstructionCases.find(
    (item) => item.id === constructionCaseId,
  );

  if (!currentConstructionCase) {
    return rejectMock('시공사례를 찾지 못했습니다.');
  }

  const nextFiles = createMockConstructionCaseFiles(input, currentConstructionCase);

  if (input.thumbnail && nextFiles.thumbnailFileUrl !== currentConstructionCase.thumbnailFileUrl) {
    revokeMockFileUrl(currentConstructionCase.thumbnailFileUrl);
  }

  if (input.galleryImages.length > 0 || input.removeGallery) {
    currentConstructionCase.galleryImages.forEach((image) => {
      revokeMockFileUrl(image.fileUrl);
    });
  }

  const updatedConstructionCase: MockConstructionCase = {
    ...currentConstructionCase,
    title: input.title,
    summary: input.summary,
    content: input.content,
    category: input.category,
    clientName: input.clientName,
    constructionStartDate: input.constructionStartDate,
    constructionEndDate: input.constructionEndDate,
    location: input.location,
    overview: input.overview,
    scopeItems: input.scopeItems,
    featurePoints: input.featurePoints,
    isFeatured: input.isFeatured,
    isPublished: input.isPublished,
    publishedAt: input.publishedAt,
    ...nextFiles,
  };

  mockConstructionCases = mockConstructionCases.map((item) => {
    return item.id === constructionCaseId ? updatedConstructionCase : item;
  });

  return resolveMock(cloneConstructionCaseDetail(updatedConstructionCase));
};

const mockUpdateConstructionCaseFeatured = (
  constructionCaseId: number,
  isFeatured: boolean,
): Promise<ConstructionCaseDetail> => {
  const currentConstructionCase = mockConstructionCases.find(
    (item) => item.id === constructionCaseId,
  );

  if (!currentConstructionCase) {
    return rejectMock('시공사례를 찾지 못했습니다.');
  }

  const currentFeaturedCount = mockConstructionCases.filter((item) => {
    return item.isFeatured && item.id !== constructionCaseId;
  }).length;

  if (isFeatured && currentFeaturedCount >= 10) {
    return rejectMock('홈 노출 시공사례는 최대 10개까지 선택할 수 있습니다.');
  }

  const updatedConstructionCase: MockConstructionCase = {
    ...currentConstructionCase,
    isFeatured,
  };

  mockConstructionCases = mockConstructionCases.map((item) => {
    return item.id === constructionCaseId ? updatedConstructionCase : item;
  });

  return resolveMock(cloneConstructionCaseDetail(updatedConstructionCase));
};

const mockDeleteConstructionCase = (constructionCaseId: number): Promise<void> => {
  const currentConstructionCase = mockConstructionCases.find(
    (item) => item.id === constructionCaseId,
  );

  if (currentConstructionCase) {
    revokeConstructionCaseImages(currentConstructionCase);
  }

  mockConstructionCases = mockConstructionCases.filter((item) => item.id !== constructionCaseId);
  return Promise.resolve();
};

const mockFetchProjectRecords = (): Promise<ProjectRecordSummary[]> => {
  return resolveMock(
    sortByPublishedAtDesc(mockProjectRecords)
      .filter((projectRecord) => projectRecord.isPublished)
      .map(toProjectRecordSummary),
  );
};

const mockFetchAdminProjectRecords = (): Promise<AdminProjectRecordSummary[]> => {
  return resolveMock(sortByPublishedAtDesc(mockProjectRecords).map(toAdminProjectRecordSummary));
};

const mockFetchAdminProjectRecord = (projectRecordId: number): Promise<ProjectRecordDetail> => {
  const projectRecord = mockProjectRecords.find((item) => item.id === projectRecordId);

  if (!projectRecord) {
    return rejectMock('공사실적을 찾지 못했습니다.');
  }

  return resolveMock(cloneProjectRecordDetail(projectRecord));
};

const mockCreateProjectRecord = (
  input: ProjectRecordMutationInput,
): Promise<ProjectRecordDetail> => {
  const projectRecord: MockProjectRecord = {
    id: getNextMockId(mockProjectRecords),
    title: input.title,
    summary: input.summary,
    category: input.category,
    clientName: input.clientName,
    constructionStartDate: input.constructionStartDate,
    constructionEndDate: input.constructionEndDate,
    content: input.content,
    isPublished: input.isPublished,
    publishedAt: input.publishedAt,
    translations: input.translations?.map((translation) => ({ ...translation })) ?? [],
    viewCount: 0,
  };

  mockProjectRecords = [projectRecord, ...mockProjectRecords];

  return resolveMock(cloneProjectRecordDetail(projectRecord));
};

const mockUpdateProjectRecord = (
  projectRecordId: number,
  input: ProjectRecordMutationInput,
): Promise<ProjectRecordDetail> => {
  const currentProjectRecord = mockProjectRecords.find((item) => item.id === projectRecordId);

  if (!currentProjectRecord) {
    return rejectMock('공사실적을 찾지 못했습니다.');
  }

  const updatedProjectRecord: MockProjectRecord = {
    ...currentProjectRecord,
    title: input.title,
    summary: input.summary,
    category: input.category,
    clientName: input.clientName,
    constructionStartDate: input.constructionStartDate,
    constructionEndDate: input.constructionEndDate,
    content: input.content,
    isPublished: input.isPublished,
    publishedAt: input.publishedAt,
    translations: input.translations?.map((translation) => ({ ...translation })) ?? [],
  };

  mockProjectRecords = mockProjectRecords.map((item) => {
    return item.id === projectRecordId ? updatedProjectRecord : item;
  });

  return resolveMock(cloneProjectRecordDetail(updatedProjectRecord));
};

const mockDeleteProjectRecord = (projectRecordId: number): Promise<void> => {
  mockProjectRecords = mockProjectRecords.filter((item) => item.id !== projectRecordId);
  return Promise.resolve();
};

const mockFetchSupportMaterials = (): Promise<SupportMaterialSummary[]> => {
  return resolveMock(
    sortByPublishedAtDesc(mockSupportMaterialList)
      .filter((supportMaterial) => supportMaterial.isPublished)
      .map(toSupportMaterialSummary),
  );
};

const mockFetchSupportMaterial = (
  supportMaterialId: number | string,
): Promise<SupportMaterialDetail> => {
  const supportMaterialKey = String(supportMaterialId);
  const supportMaterial = mockSupportMaterialList.find(
    (item) =>
      item.isPublished &&
      (String(item.id) === supportMaterialKey || item.slug === supportMaterialKey),
  );

  if (!supportMaterial) {
    return rejectMock('기술자료를 찾지 못했습니다.');
  }

  supportMaterial.viewCount += 1;

  return resolveMock(cloneSupportMaterialDetail(supportMaterial));
};

const mockFetchAdminSupportMaterials = (): Promise<AdminSupportMaterialSummary[]> => {
  return resolveMock(
    sortByPublishedAtDesc(mockSupportMaterialList).map(toAdminSupportMaterialSummary),
  );
};

const mockFetchAdminSupportMaterial = (
  supportMaterialId: number,
): Promise<SupportMaterialDetail> => {
  const supportMaterial = mockSupportMaterialList.find((item) => item.id === supportMaterialId);

  if (!supportMaterial) {
    return rejectMock('기술자료를 찾지 못했습니다.');
  }

  return resolveMock(cloneSupportMaterialDetail(supportMaterial));
};

const mockCreateSupportMaterial = (
  input: SupportMaterialMutationInput,
): Promise<SupportMaterialDetail> => {
  const asset = createMockSupportMaterialAsset(input);
  const supportMaterial: MockSupportMaterial = {
    id: getNextMockId(mockSupportMaterialList),
    slug: crypto.randomUUID(),
    title: input.title,
    description: input.description,
    category: input.category,
    categoryCode: toSupportMaterialCategoryCode(input.category),
    content: input.content,
    isPublished: input.isPublished,
    publishedAt: input.publishedAt,
    viewCount: 0,
    ...asset,
  };

  mockSupportMaterialList = [supportMaterial, ...mockSupportMaterialList];

  return resolveMock(cloneSupportMaterialDetail(supportMaterial));
};

const mockUpdateSupportMaterial = (
  supportMaterialId: number,
  input: SupportMaterialMutationInput,
): Promise<SupportMaterialDetail> => {
  const currentSupportMaterial = mockSupportMaterialList.find(
    (item) => item.id === supportMaterialId,
  );

  if (!currentSupportMaterial) {
    return rejectMock('기술자료를 찾지 못했습니다.');
  }

  const nextAsset = createMockSupportMaterialAsset(input, currentSupportMaterial);

  if (
    input.assetKind === 'FILE' &&
    input.file &&
    nextAsset.fileUrl !== currentSupportMaterial.fileUrl
  ) {
    revokeMockFileUrl(currentSupportMaterial.fileUrl);
  }

  const updatedSupportMaterial: MockSupportMaterial = {
    ...currentSupportMaterial,
    title: input.title,
    description: input.description,
    category: input.category,
    categoryCode: toSupportMaterialCategoryCode(input.category),
    content: input.content,
    isPublished: input.isPublished,
    publishedAt: input.publishedAt,
    ...nextAsset,
  };

  mockSupportMaterialList = mockSupportMaterialList.map((item) => {
    return item.id === supportMaterialId ? updatedSupportMaterial : item;
  });

  return resolveMock(cloneSupportMaterialDetail(updatedSupportMaterial));
};

const mockDeleteSupportMaterial = (supportMaterialId: number): Promise<void> => {
  const currentSupportMaterial = mockSupportMaterialList.find(
    (item) => item.id === supportMaterialId,
  );

  if (currentSupportMaterial) {
    revokeMockFileUrl(currentSupportMaterial.fileUrl);
  }

  mockSupportMaterialList = mockSupportMaterialList.filter((item) => item.id !== supportMaterialId);
  return Promise.resolve();
};

const mockFetchAdminInquiries = (): Promise<AdminInquirySummary[]> => {
  return resolveMock(sortByCreatedAtDesc(mockInquiries).map(toAdminInquirySummary));
};

const mockFetchAdminInquiry = (inquiryId: number): Promise<AdminInquiryDetail> => {
  const inquiry = mockInquiries.find((item) => item.id === inquiryId);

  if (!inquiry) {
    return rejectMock('문의를 찾지 못했습니다.');
  }

  return resolveMock({ ...inquiry });
};

const mockProcessInquiry = (
  inquiryId: number,
  input: AdminInquiryProcessInput,
): Promise<AdminInquiryDetail> => {
  const inquiry = mockInquiries.find((item) => item.id === inquiryId);

  if (!inquiry) {
    return rejectMock('문의를 찾지 못했습니다.');
  }

  const now = new Date().toISOString();
  const isDone = input.status === 'DONE';
  const nextInquiry: MockInquiry = {
    ...inquiry,
    status: input.status,
    adminMemo: input.adminMemo,
    assignedAdminUsername: input.assignedAdminUsername,
    respondedAt: isDone ? (inquiry.respondedAt ?? now) : null,
    updatedAt: now,
  };

  mockInquiries = mockInquiries.map((item) => {
    return item.id === inquiryId ? nextInquiry : item;
  });

  return resolveMock({ ...nextInquiry });
};

export const loginMockAdmin = (credentials: {
  username: string;
  password: string;
}): Promise<AdminLoginResponse> => {
  if (!credentials.username.trim() || !credentials.password.trim()) {
    throw new Error('아이디와 비밀번호를 입력해 주세요.');
  }

  return Promise.resolve({
    accessToken: mockAdminAccessToken,
    tokenType: 'Bearer',
    expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 8).toISOString(),
  });
};

export const refreshMockAdminSession = (): Promise<AdminLoginResponse> => {
  return Promise.resolve({
    accessToken: mockAdminAccessToken,
    tokenType: 'Bearer',
    expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 8).toISOString(),
  });
};

export const logoutMockAdminSession = async (): Promise<void> => {
  return Promise.resolve();
};

export {
  mockCreateConstructionCase as createMockConstructionCase,
  mockCreatePopup as createMockPopup,
  mockCreateProjectRecord as createMockProjectRecord,
  mockCreateSupportMaterial as createMockSupportMaterial,
  mockDeleteConstructionCase as deleteMockConstructionCase,
  mockDeletePopup as deleteMockPopup,
  mockDeleteProjectRecord as deleteMockProjectRecord,
  mockDeleteSupportMaterial as deleteMockSupportMaterial,
  mockFetchAdminConstructionCase as fetchMockAdminConstructionCase,
  mockFetchAdminConstructionCases as fetchMockAdminConstructionCases,
  mockFetchAdminInquiry as fetchMockAdminInquiry,
  mockFetchAdminInquiries as fetchMockAdminInquiries,
  mockFetchAdminPopup as fetchMockAdminPopup,
  mockFetchAdminPopups as fetchMockAdminPopups,
  mockFetchAdminProjectRecord as fetchMockAdminProjectRecord,
  mockFetchAdminProjectRecords as fetchMockAdminProjectRecords,
  mockFetchAdminSupportMaterial as fetchMockAdminSupportMaterial,
  mockFetchAdminSupportMaterials as fetchMockAdminSupportMaterials,
  mockFetchConstructionCase as fetchMockConstructionCase,
  mockFetchConstructionCases as fetchMockConstructionCases,
  mockFetchProjectRecords as fetchMockProjectRecords,
  mockFetchSupportMaterial as fetchMockSupportMaterial,
  mockFetchSupportMaterials as fetchMockSupportMaterials,
  mockProcessInquiry as processMockInquiry,
  mockUpdateConstructionCase as updateMockConstructionCase,
  mockUpdateConstructionCaseFeatured as updateMockConstructionCaseFeatured,
  mockUpdatePopup as updateMockPopup,
  mockUpdateProjectRecord as updateMockProjectRecord,
  mockUpdateSupportMaterial as updateMockSupportMaterial,
};
